/* ────────────────────────────────────────────────────────────────────────────
 * LOCAL STORE
 * ────────────────────────────────────────────────────────────────────────────
 * A tiny external store (subscribe/getSnapshot) persisted to localStorage.
 * Holds the simulated credit balance, the purchase ledger, generation history
 * and the engine settings. No server, no account, no network.
 * ──────────────────────────────────────────────────────────────────────────── */

import { useSyncExternalStore } from 'react';
import type {
  GenerationMode,
  GenerationSettings,
} from '../generation/types';
import { REFERENCE_SETTINGS } from '../lib/pricing';
import { makeId } from '../lib/format';

// v2: credit economy + settings shape changed (aspect ratio, model id).
const STORAGE_KEY = 'atelier.state.v2';

/** Credits granted on first run so the app is usable straight away. */
const WELCOME_CREDITS = 100;

// Generous on purpose: this is the user's whole generation record. localStorage
// has plenty of room (a few MB) and each entry is mostly a small JPEG thumbnail.
const MAX_HISTORY = 100;

export interface HistoryEntry {
  id: string;
  createdAt: string;
  mode: GenerationMode;
  prompt: string;
  settings: GenerationSettings;
  cost: number;
  seed: number;
  filename: string;
  mimeType: string;
  durationSeconds: number;
  width: number;
  height: number;
  /** Small persisted JPEG thumbnail. */
  thumbUrl: string;
  isStillFallback?: boolean;
  /** Which engine produced it, e.g. "maestro" or "mock". */
  providerId: string;
  /**
   * Runtime only, never persisted: object URLs die with the page, so a
   * reloaded history entry keeps its metadata + thumbnail but not the clip.
   */
  videoUrl?: string;
}

export type LedgerKind = 'welcome' | 'purchase' | 'spend' | 'refund';

export interface LedgerEntry {
  id: string;
  createdAt: string;
  kind: LedgerKind;
  /** Signed: positive = credited, negative = spent. */
  credits: number;
  euro?: number;
  bonusCredits?: number;
  note: string;
}

export interface AppState {
  credits: number;
  ledger: LedgerEntry[];
  history: HistoryEntry[];
  settings: GenerationSettings;
  mode: GenerationMode;
}

function initialState(): AppState {
  return {
    credits: WELCOME_CREDITS,
    ledger: [
      {
        id: makeId('led'),
        createdAt: new Date().toISOString(),
        kind: 'welcome',
        credits: WELCOME_CREDITS,
        note: 'Welcome credits — take the studio for a spin',
      },
    ],
    history: [],
    settings: { ...REFERENCE_SETTINGS },
    mode: 'text-to-video',
  };
}

function load(): AppState {
  if (typeof localStorage === 'undefined') return initialState();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialState();
    const parsed = JSON.parse(raw) as Partial<AppState>;
    const base = initialState();
    return {
      credits: typeof parsed.credits === 'number' ? parsed.credits : base.credits,
      ledger: Array.isArray(parsed.ledger) ? parsed.ledger : base.ledger,
      history: Array.isArray(parsed.history)
        ? parsed.history.map((h) => ({ ...h, videoUrl: undefined }))
        : [],
      settings: { ...base.settings, ...(parsed.settings ?? {}) },
      mode: parsed.mode === 'image-to-video' ? 'image-to-video' : 'text-to-video',
    };
  } catch {
    return initialState();
  }
}

let state: AppState = load();
const listeners = new Set<() => void>();

/*
 * Cross-tab sync. localStorage writes in one tab do NOT update the `state`
 * variable held in another tab's JS runtime — each tab reads it once at
 * load. Without this, two tabs on the SAME origin visibly disagree (e.g. one
 * shows 100 credits, the other 80) until the stale one is reloaded. The
 * browser fires a `storage` event on every OTHER tab when localStorage
 * changes, so we just reload from it and notify React.
 *
 * This does NOT help across different origins (e.g. localhost:5173 vs
 * localhost:4173) — those are separate storage buckets by browser design,
 * no workaround possible. That's what actions.exportState/importState below
 * are for.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;
    state = load();
    listeners.forEach((l) => l());
  });
}

function persist(next: AppState): void {
  if (typeof localStorage === 'undefined') return;
  const serialisable: AppState = {
    ...next,
    // Strip runtime-only object URLs before writing.
    history: next.history.map(({ videoUrl: _drop, ...rest }) => rest),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(serialisable));
  } catch {
    // Quota exceeded — drop thumbnails from the oldest entries and retry once.
    try {
      const slim: AppState = {
        ...serialisable,
        history: serialisable.history.map((h, i) => (i > 5 ? { ...h, thumbUrl: '' } : h)),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(slim));
    } catch {
      /* give up silently — the app still works for this session */
    }
  }
}

function setState(updater: (prev: AppState) => AppState): void {
  state = updater(state);
  persist(state);
  listeners.forEach((l) => l());
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => state;

export function useAppState(): AppState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/* ── Actions ──────────────────────────────────────────────────────────────── */

export const actions = {
  /** Simulated purchase — credits appear locally, no money moves. */
  purchase(euro: number, credits: number, bonusCredits: number): void {
    setState((prev) => ({
      ...prev,
      credits: prev.credits + credits,
      ledger: [
        {
          id: makeId('led'),
          createdAt: new Date().toISOString(),
          kind: 'purchase' as const,
          credits,
          euro,
          bonusCredits,
          note:
            bonusCredits > 0
              ? `€${euro} pack — ${credits - bonusCredits} + ${bonusCredits} bonus credits`
              : `€${euro} pack — ${credits} credits`,
        },
        ...prev.ledger,
      ].slice(0, 60),
    }));
  },

  /** Deducts credits for a generation. Returns false when the balance is short. */
  spend(credits: number, note: string): boolean {
    if (state.credits < credits) return false;
    setState((prev) => ({
      ...prev,
      credits: prev.credits - credits,
      ledger: [
        {
          id: makeId('led'),
          createdAt: new Date().toISOString(),
          kind: 'spend' as const,
          credits: -credits,
          note,
        },
        ...prev.ledger,
      ].slice(0, 60),
    }));
    return true;
  },

  /** Gives credits back — used when a job is cancelled or fails. */
  refund(credits: number, note: string): void {
    setState((prev) => ({
      ...prev,
      credits: prev.credits + credits,
      ledger: [
        {
          id: makeId('led'),
          createdAt: new Date().toISOString(),
          kind: 'refund' as const,
          credits,
          note,
        },
        ...prev.ledger,
      ].slice(0, 60),
    }));
  },

  addHistory(entry: HistoryEntry): void {
    setState((prev) => {
      const next = [entry, ...prev.history];
      // Free the object URLs of anything falling off the end of the list.
      next.slice(MAX_HISTORY).forEach((h) => {
        if (h.videoUrl?.startsWith('blob:')) URL.revokeObjectURL(h.videoUrl);
      });
      return { ...prev, history: next.slice(0, MAX_HISTORY) };
    });
  },

  removeHistory(id: string): void {
    setState((prev) => {
      const target = prev.history.find((h) => h.id === id);
      if (target?.videoUrl?.startsWith('blob:')) URL.revokeObjectURL(target.videoUrl);
      return { ...prev, history: prev.history.filter((h) => h.id !== id) };
    });
  },

  clearHistory(): void {
    setState((prev) => {
      prev.history.forEach((h) => {
        if (h.videoUrl?.startsWith('blob:')) URL.revokeObjectURL(h.videoUrl);
      });
      return { ...prev, history: [] };
    });
  },

  setSettings(patch: Partial<GenerationSettings>): void {
    setState((prev) => ({ ...prev, settings: { ...prev.settings, ...patch } }));
  },

  setMode(mode: GenerationMode): void {
    setState((prev) => ({ ...prev, mode }));
  },

  /** Wipes everything back to first-run state. */
  resetAll(): void {
    setState((prev) => {
      prev.history.forEach((h) => {
        if (h.videoUrl?.startsWith('blob:')) URL.revokeObjectURL(h.videoUrl);
      });
      return initialState();
    });
  },

  /**
   * Serialises the current state for hand-carrying between origins — e.g.
   * localhost:5173 (dev server) and localhost:4173 (the always-on build) are
   * different browser storage buckets with no other way to reconcile them.
   */
  exportState(): string {
    return JSON.stringify(
      { ...state, history: state.history.map(({ videoUrl: _drop, ...rest }) => rest) },
      null,
      2,
    );
  },

  /**
   * Merges an exported state into the current one — used to recombine data
   * that split across two origins/browsers. Never discards anything: history
   * and ledger are unioned by id, and the credit balance is recomputed from
   * the merged ledger (sum of every distinct real event) rather than picked
   * from one side, so nothing is silently dropped or double-counted.
   */
  importAndMergeState(json: string): { addedHistory: number; addedLedger: number } {
    const incoming = JSON.parse(json) as Partial<AppState>;
    const incomingHistory = Array.isArray(incoming.history) ? incoming.history : [];
    const incomingLedger = Array.isArray(incoming.ledger) ? incoming.ledger : [];

    let addedHistory = 0;
    let addedLedger = 0;

    setState((prev) => {
      const historyIds = new Set(prev.history.map((h) => h.id));
      const mergedHistory = [
        ...prev.history,
        ...incomingHistory
          .filter((h) => !historyIds.has(h.id))
          .map((h) => ({ ...h, videoUrl: undefined })),
      ].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      addedHistory = mergedHistory.length - prev.history.length;

      const ledgerIds = new Set(prev.ledger.map((l) => l.id));
      const newLedgerEntries = incomingLedger.filter((l) => !ledgerIds.has(l.id));
      addedLedger = newLedgerEntries.length;
      let mergedLedger = [...prev.ledger, ...newLedgerEntries].sort((a, b) =>
        b.createdAt.localeCompare(a.createdAt),
      );

      // Every origin grants its own "welcome" bonus with its own id, so a
      // plain id-union double-counts it once per origin merged in. It's a
      // one-time-ever grant by definition — keep only the earliest.
      const welcomeEntries = mergedLedger
        .filter((l) => l.kind === 'welcome')
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      if (welcomeEntries.length > 1) {
        const dropIds = new Set(welcomeEntries.slice(1).map((l) => l.id));
        mergedLedger = mergedLedger.filter((l) => !dropIds.has(l.id));
      }

      // Recompute the balance from the merged ledger instead of trusting
      // either side's `credits` number — this is the only way the result is
      // guaranteed consistent with the (deduplicated) event history.
      const recomputedCredits = mergedLedger.reduce((sum, l) => sum + l.credits, 0);

      return {
        ...prev,
        credits: recomputedCredits,
        ledger: mergedLedger.slice(0, 300),
        history: mergedHistory.slice(0, MAX_HISTORY),
      };
    });

    return { addedHistory, addedLedger };
  },
};
